import {EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {FormHelperService} from '@services/form-helper.service';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

/**
 * Базовый класс для компонентов, являющихся вложенной формой.
 * Предоставляет только визуальное отображение и структуру полей.
 * Валидация, восстановление и обработка сохранения остается в компоненте, использующим данный
 */

export abstract class SubFormComponent implements OnInit, OnDestroy {
    /**
     * Компонент можно использовать как самостоятельную форму и как значение для поля другой формы.
     * Если пеердана группа form, значит компонент будет использоваться в качестве значения этой группы form
     */
    @Input() form: FormGroup;

    /*
    * Событие, что текущая валидность этой подформы изменилась
    * */
    @Output() validStatusChange = new EventEmitter<boolean>();

    /**
     * Делаем так, а не через DI чтобы не переделывать конструкторы
     * @type {FormHelperService}
     */
    public formHelperService = new FormHelperService();

    /* В этом свойстве будет описан набор полей реактивной формы */
    public abstract formGroupTemplate: FormGroup;

    /*
    * Поля подформы, по которым будем судить, валидная эта подформа или нет.
    * Так как форма может быть плоской и состоять при этом из нескольких подформ, нам нужно как-то выделить поля, принадлежащие
    * именно этой подформе. Тут и будем хранить имена полей текущей подформы
    * */
    protected fields: string[] = [];

    private previousFormValidStatus;

    protected subs: Subscription[] = [];

    protected constructor() {}

    ngOnInit() {
        this.init();

        this.afterInit();

        /*
        * Запоминаем имена полей, принадлежащие именно этой подформе
        * */
        this.fields = Object.keys(this.formGroupTemplate.controls);

        const sub: any = this.form.statusChanges.pipe(
            /**
             * Разражаем проверки, чтобы реагировать на изменения не чаще, чем в за заданное время.
             * Если без разрядки, то могут быть ситуации, когда перед отправкой формы мы сбросили ошибки и после ответа показали их.
             * Нам такие пустые вычисления не нужны, поэтому берем последние изменения через 100ms
             */
            debounceTime(100),
        ).subscribe((status: string) => {
            const valid = this.getValidity();
            /**
             * Если валидность формы не поменялась, то и делать ничего не нужно
             */
            if (this.previousFormValidStatus === valid) {
                return;
            }

            this.previousFormValidStatus = valid;

            this.validStatusChange.emit(valid);
        });

        this.subs.push(sub);
    }

    afterInit() {

    }

    /**
     * Описываем структура данных удостоверения.
     * 1. Если form не был передан, значит удостоверение является самостоятельной формой и инициализируем форму из formGroupTemplate.
     * 2. Если же удостоверение будет являться значением другой группы, то инициализируем как значение группы. В этом
     * случае нужно проверять, есть ли уже поля formGroupTemplate в этой группе.
     * Если поле есть, то берем его значение, а если нет, то добавляем поле к группе.
     * Это сделано для того, чтобы значения можно было восстанавливать или подставлять при инициализации.
     */
    init() {
        if (!this.form) {
            this.form = this.formGroupTemplate;
        } else {
            // Пробегаемся по структуре и добавляем недостающие поля к группе
            for (const key of Object.keys(this.formGroupTemplate.controls)) {
                if (!this.form.controls[key]) {
                    this.form.addControl(key, this.formGroupTemplate.controls[key]);
                    continue;
                }

                /**
                 * Если поле в форме и в шаблоне является контролом, то
                 * если восстанавливаемое значение равно null или пустой строке '',
                 * но при этом в шаблоне формы есть какое-то значение по-умолчанию (не равное null или пустой строке ''),
                 * то применяем к полю формы значение, указанное по-умолчанию в шаблоне
                 */
                if (this.form.controls[key] instanceof FormControl && this.formGroupTemplate.controls[key] instanceof FormControl) {
                    if ((this.form.controls[key].value === null || this.form.controls[key].value === '')
                        && (this.formGroupTemplate.controls[key].value !== null || this.formGroupTemplate.controls[key].value !== '')) {
                        this.form.controls[key].patchValue(this.formGroupTemplate.controls[key].value);
                    }
                }

                /**
                 * Если от backend пришел контрол (скалярное значение), но по шаблону он должен быть массивом,
                 * то восстанавливаем массив вместо контрола.
                 * Например, у нас должен быть по-умолчанию массив из одной группы,
                 * а с backend пришел null или другое скалярное значение.
                 * В таком случае это скалярное значение заменяем на массив
                 */
                if (this.form.controls[key] instanceof FormControl && this.formGroupTemplate.controls[key] instanceof FormArray) {
                    this.form.setControl(key, this.formGroupTemplate.controls[key]);
                }

                /**
                 * Если на шаблоне контрола был фронтендовский валидатор заполненности поля,
                 * то навешиваем его на новый или восстановленный контрол
                 * @type {ValidatorFn}
                 */
                const validator = this.formGroupTemplate.controls[key].validator;
                if (validator) {
                    this.form.controls[key].setValidators(validator);
                }
            }
        }
    }

    ngOnDestroy() {
        this.subs.forEach(sub => sub.unsubscribe());
    }

    /**
     * Удалить вложенную форму (себя) из родительского formArray.
     */
    public removeSelfFromParentArray() {
        /**
         * Если нет родителя или это не FormArray - ничего не делаем.
         */
        if (!this.form.parent || !(this.form.parent instanceof FormArray)) {
            return;
        }

        // Находим "себя" в FormArray
        const array = <FormArray>this.form.parent;
        const selfIndex = array.controls.indexOf(this.form);

        // Удаляем "себя", если есть в коллекции
        if (selfIndex >= 0) {
            array.removeAt(selfIndex);
        }
    }

    protected resetForm(markTouched = true) {
        this.fields.forEach(key => {
            const control = this.form.get(key);
            if (control instanceof FormControl) {
                control.patchValue('');

                if (markTouched) {
                    control.markAsDirty();
                    control.markAsTouched();
                }

                control.updateValueAndValidity();
            }

            if (control instanceof FormArray) {
                while (control.length) {
                    control.removeAt(0);
                }
            }
        });
    }

    /**
     * Вычислить валидность текущей подформы.
     * Проверяем поля, принадлежащие только этой подформе.
     *
     * Задизейбленные поля считаются валидными
     * @returns {boolean}
     */
    private getValidity() {
        return this.fields.every(field => this.form.get(field).valid || this.form.get(field).disabled);
    }
}
